import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/utils/axios";
import cls from "./AdminCities.module.scss";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "—";
  }
};

export default function AdminCities() {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const fallbackImg = "https://dummyimage.com/120x80/1a1a1a/ffffff&text=City";

  const fetchCities = useCallback(async () => {
    try {
      setLoading(true);
      setErr("");
      const { data } = await api.get("/v1/admin/cities");
      setCities(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load cities", e);
      const msg =
        e?.response?.data?.message || e?.message || "Could not load cities.";
      setErr(msg);
      setCities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  const getImg = (city) => {
    if (!city) return fallbackImg;

    // Match controller: avatarThumb and avatar are objects { key, url }
    const thumbUrl = city.avatarThumb?.url;
    const avatarUrl = city.avatar?.url;

    return thumbUrl || avatarUrl || fallbackImg;
  };

  return (
    <div className={cls.wrap}>
      <header className={cls.header}>
        <div>
          <p className={cls.eyebrow}>Admin · Cities</p>
          <h1 className="nu-title is-mr" data-glow="1">
            Cities
          </h1>
        </div>
        <div className={cls.actions}>
          <button
            className="nu-btn nu-btn--ghost"
            type="button"
            onClick={fetchCities}
          >
            Refresh
          </button>
          <Link className="nu-btn nu-btn--primary" to="/admin/cities/create">
            + Create City
          </Link>
        </div>
      </header>

      {err && <div className={cls.error}>⚠ {err}</div>}

      <div className={cls.grid} aria-busy={loading ? "true" : "false"}>
        {loading ? (
          [...Array(6)].map((_, i) => (
            <div key={`sk-${i}`} className={`${cls.card} ${cls.skeleton}`}>
              <div className={cls.skelThumb} />
              <div className={cls.skelLine} />
              <div className={cls.skelLineWide} />
            </div>
          ))
        ) : cities.length === 0 ? (
          <div className={cls.empty}>No cities yet. Create the first one.</div>
        ) : (
          cities.map((city) => (
            <article key={city._id} className={cls.card}>
              <div className={cls.thumbWrap}>
                <img
                  src={getImg(city)}
                  alt={city.cityName || "City avatar"}
                  className={cls.thumb}
                />
                <div className={cls.status} data-status={city.cityStatus}>
                  {city.cityStatus || "unknown"}
                </div>
              </div>

              <div className={cls.meta}>
                <div className={cls.titleRow}>
                  <h3>{city.cityName || "Untitled"}</h3>
                  <span className={cls.lang}>
                    {city.cityLanguage?.toUpperCase?.() || "EN"}
                  </span>
                </div>
                <p className={cls.sub}>{city.cityVibe || "No vibe set."}</p>
                <p className={cls.updated}>
                  Updated {formatDate(city.updatedAt)}
                </p>
              </div>

              <div className={cls.actionsRow}>
                {/* Streets button pinned LEFT */}
                <Link
                  className="nu-btn nu-btn--green"
                  style={{ marginRight: "auto" }}
                  to={`/admin/cities/single/${city._id}/streets`}
                >
                  Streets
                </Link>

                {/* View button stays RIGHT */}
                <Link
                  className="nu-btn nu-btn--ghost"
                  to={`/admin/cities/single/${city._id}`}
                >
                  View
                </Link>
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
}
